import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import QRCode from "qrcode";
import {
  useDecaDetail,
  useDeleteDeca,
  useMe,
  usePdfUrl,
  useVersionPdf,
  useSignatures,
  useCloseDeca,
  useReopenDeca,
  useUpdateDecaAssignments,
  useDrivers,
  useVehicles,
  useCounterparties,
} from "../api/hooks";
import { AdrPlacard, Button, Card, Chip, ErrorNotice, Notice, PageHeader, Spinner, StatusStamp } from "../components/ui";
import { decaStatusLabel, decaStatusTone, formatDate, formatDateTime, formatHash } from "../lib/format";
import { IncidentPanel } from "../components/IncidentPanel";
import { SignaturePanel } from "../components/SignaturePanel";

/**
 * Ficha del documento: PDF, QR, historial y controles.
 *
 * El QR que se muestra es el mismo que va impreso en el PDF —la URL pública de
 * descarga sin login—, así que sirve para comprobar que un lector de campo
 * resuelve al documento correcto.
 */
export function DecaDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const detail = useDecaDetail(id ?? null);
  const pdf = usePdfUrl(id ?? "");
  const versionPdf = useVersionPdf();
  const signatures = useSignatures(id ?? null);
  const closeDeca = useCloseDeca();
  const reopenDeca = useReopenDeca();
  const deleteDeca = useDeleteDeca();
  const updateAssignments = useUpdateDecaAssignments();
  const me = useMe();
  const isAdmin = me.data?.role === "ADMIN";
  const drivers = useDrivers();
  const vehicles = useVehicles();
  const counterparties = useCounterparties();
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Asignaciones
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [assignSuccess, setAssignSuccess] = useState<string | null>(null);
  // Campos del formulario de asignaciones
  const [assignDriverId, setAssignDriverId] = useState<string>("");
  const [assignVehicleId, setAssignVehicleId] = useState<string>("");
  const [assignOperadorMode, setAssignOperadorMode] = useState<"" | "catalog" | "clear">(""); 
  const [assignOperadorId, setAssignOperadorId] = useState<string>("");
  const [assignDestinatarioMode, setAssignDestinatarioMode] = useState<"" | "catalog" | "clear">("");
  const [assignDestinatarioId, setAssignDestinatarioId] = useState<string>("");
  const [assignShipmentIndex, setAssignShipmentIndex] = useState<number>(0);
  const [assignReason, setAssignReason] = useState<string>("");

  const publicUrl = useMemo(() => {
    if (!detail.data) return null;
    return `${window.location.origin}/d/${detail.data.document.urlToken}`;
  }, [detail.data]);

  const currentVersion = useMemo(() => {
    if (!detail.data) return null;
    const entries = detail.data.chain.flatMap((entry) => entry.versions);
    return entries.at(-1) ?? null;
  }, [detail.data]);

  useEffect(() => {
    if (!publicUrl) return;
    let cancelled = false;
    void QRCode.toDataURL(publicUrl, { width: 220, margin: 1 }).then((url) => {
      if (!cancelled) setQrDataUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [publicUrl]);

  if (detail.isLoading) return <Spinner label="Cargando el documento" />;
  if (detail.isError) return <ErrorNotice error={detail.error} onRetry={() => void detail.refetch()} />;
  if (!detail.data) return null;

  const { document, chain, dataSnapshot } = detail.data;

  // Lógica espejo de closeDeca.ts en la API
  // isClosed: cerrado manualmente (closedAt) O automáticamente (transportEndDate pasó)
  const now = new Date();
  const transportEndDate = document.transportEndDate ? new Date(document.transportEndDate) : null;

  // downloadDisabledAt: si la BD lo tiene, usarlo. Si no, calcularlo como
  // transportEndDate + 7 días (el barrido diario puede no haber pasado aún,
  // pero para DECAs con transportEndDate ya vencido el valor teórico es
  // suficiente para decidir si el plazo de reapertura sigue abierto).
  // Sin este cálculo, un downloadDisabledAt=null con transportEndDate del
  // año pasado haría aparecer el botón "Reabrir" incorrectamente.
  const DOWNLOAD_DISABLE_DAYS = 7;
  const downloadDisabledAt = document.downloadDisabledAt
    ? new Date(document.downloadDisabledAt)
    : transportEndDate
      ? new Date(transportEndDate.getTime() + DOWNLOAD_DISABLE_DAYS * 24 * 60 * 60 * 1000)
      : null;

  const reopenedAt = document.reopenedAt ? new Date(document.reopenedAt) : null;

  // Cierre automático por fecha: aplica solo si no fue reabierto manualmente después
  const isAutoClosedByDate =
    !document.closedAt &&
    transportEndDate !== null &&
    transportEndDate < now &&
    !(reopenedAt && reopenedAt.getTime() > transportEndDate.getTime());

  const isClosed = Boolean(document.closedAt) || isAutoClosedByDate;

  // canClose: no cerrado Y ACTIVO.
  // Si fue reabierto manualmente (reopenedAt > transportEndDate) se permite cerrar
  // aunque transportEndDate ya haya pasado, siempre que siga dentro del plazo de descarga.
  const wasManuallyReopened = Boolean(reopenedAt && transportEndDate && reopenedAt.getTime() > transportEndDate.getTime());
  const canClose =
    !isClosed &&
    document.status === "ACTIVO" &&
    (transportEndDate === null || transportEndDate >= now || wasManuallyReopened);

  // canReopen: está cerrado Y dentro del plazo de descarga
  // - Sin transportEndDate: solo si fue cierre manual (closedAt existe)
  // - Con transportEndDate: downloadDisabledAt (real o teórico) > now
  const canReopen = isClosed && (
    transportEndDate === null
      ? Boolean(document.closedAt)
      : (downloadDisabledAt !== null && downloadDisabledAt > now)
  );

  // canDeleteDeca: ADMIN + ACTIVO + transporte no iniciado aún + sin firmas
  // Se permite borrar si no hay transportStartDate O si es una fecha futura.
  // La condición de documentos hijos la valida el backend (responde 409).
  const signaturesCount = signatures.data?.items.length ?? 0;
  const transportStarted =
    !!document.transportStartDate && new Date(document.transportStartDate) <= now;
  const canDeleteDeca =
    isAdmin &&
    document.status === "ACTIVO" &&
    !transportStarted &&
    signaturesCount === 0;

  return (
    <>
      <PageHeader
        eyebrow="Documento"
        title={document.urlToken}
        description={`Emitido el ${formatDateTime(document.createdAt)} · última modificación ${formatDateTime(document.updatedAt)}`}
        actions={
          <>
            <Button onClick={() => navigate("/deca")}>Volver al listado</Button>
            {canClose ? (
              <Button
                variant="quiet"
                disabled={closeDeca.isPending}
                onClick={() => {
                  setCloseError(null);
                  closeDeca.mutate(document.id, {
                    onError: (err) => setCloseError(err instanceof Error ? err.message : "Error al cerrar el DeCA"),
                  });
                }}
              >
                {closeDeca.isPending ? "Cerrando…" : "🔒 Cerrar DeCA"}
              </Button>
            ) : null}
            {canReopen ? (
              <Button
                variant="quiet"
                disabled={reopenDeca.isPending}
                onClick={() => {
                  setCloseError(null);
                  reopenDeca.mutate(document.id, {
                    onError: (err) => setCloseError(err instanceof Error ? err.message : "Error al reabrir el DeCA"),
                  });
                }}
              >
                {reopenDeca.isPending ? "Reabriendo…" : "🔓 Reabrir DeCA"}
              </Button>
            ) : null}
            <Button variant="primary" disabled={pdf.isPending} onClick={() => pdf.mutate()}>
              {pdf.isPending ? "Preparando…" : "Ver PDF"}
            </Button>
            {canDeleteDeca ? (
              <Button
                variant="quiet"
                disabled={deleteDeca.isPending}
                onClick={() => {
                  setDeleteConfirm(true);
                  setDeleteError(null);
                }}
              >
                🗑 Eliminar DeCA
              </Button>
            ) : null}
          </>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <StatusStamp status={document.status} />
        <Chip tone={decaStatusTone(document.status)}>{decaStatusLabel(document.status)}</Chip>
        {isClosed ? (
          <span className="inline-flex items-center rounded-full border border-purple-300 bg-purple-50 px-2.5 py-0.5 text-xs font-semibold text-purple-800 dark:border-purple-700 dark:bg-purple-950 dark:text-purple-200">
            🔒 Cerrado
          </span>
        ) : null}
        {document.hasIncidents ? <Chip tone="danger">Con incidencia</Chip> : null}
        {document.relatedDocumentId ? <Chip tone="neutral">Sustituye a otro documento</Chip> : null}
      </div>

      {isClosed ? (
        <div className="mb-4">
          <Notice tone="warn">
            <span className="font-semibold">🔒 DeCA cerrado</span>
            {document.closedAt ? (
              <> — cerrado el {formatDateTime(document.closedAt)}</>  
            ) : (
              <> — cerrado automáticamente por fecha de fin de transporte</>
            )}
            {canReopen ? <> · Puedes reabrirlo mientras el plazo de descarga no haya vencido.</> : null}
          </Notice>
        </div>
      ) : null}

      {closeError ? (
        <div className="mb-4">
          <Notice tone="danger">{closeError}</Notice>
        </div>
      ) : null}

      {deleteConfirm ? (
        <div className="mb-4 rounded-md border border-red-300 bg-red-50 p-4 dark:border-red-800 dark:bg-red-950/30">
          <p className="text-sm font-semibold text-red-700 dark:text-red-400">
            ¿Eliminar definitivamente este DeCA?
          </p>
          <p className="mt-1 text-xs text-red-600 dark:text-red-300">
            Solo es posible si el documento está activo, no tiene fecha de inicio de transporte, no
            tiene firmas y no tiene documentos hijos. Esta acción <strong>no se puede deshacer</strong>.
          </p>
          {deleteError ? (
            <p className="mt-2 text-xs font-medium text-red-700 dark:text-red-400">{deleteError}</p>
          ) : null}
          <div className="mt-3 flex gap-2">
            <Button
              variant="primary"
              disabled={deleteDeca.isPending}
              onClick={() => {
                setDeleteError(null);
                deleteDeca.mutate(document.id, {
                  onError: (err) =>
                    setDeleteError(err instanceof Error ? err.message : "Error al eliminar el DeCA"),
                });
              }}
            >
              {deleteDeca.isPending ? "Eliminando…" : "Eliminar definitivamente"}
            </Button>
            <Button onClick={() => setDeleteConfirm(false)}>Cancelar</Button>
          </div>
        </div>
      ) : null}

      {document.status === "DESCARGA_DESACTIVADA" ? (
        <div className="mb-4">
          <Notice tone="warn">
            La descarga pública directa está desactivada desde{" "}
            {formatDateTime(document.downloadDisabledAt)}. El documento sigue conservado y desde el
            panel se puede consultar y descargar con normalidad; la retención mínima de un año se
            mantiene.
          </Notice>
        </div>
      ) : null}

      {document.hasIncidents && document.incidentNotes ? (
        <div className="mb-4">
          <Notice tone="danger">
            <span className="font-semibold">Incidencia registrada:</span> {document.incidentNotes}
          </Notice>
        </div>
      ) : null}

      {pdf.isError ? (
        <div className="mb-4">
          <ErrorNotice error={pdf.error} />
        </div>
      ) : null}

      {versionPdf.isError ? (
        <div className="mb-4">
          <ErrorNotice error={versionPdf.error} />
        </div>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <h2 className="micro-label mb-2">Versión vigente</h2>
          {pdf.data?.url ? (
            <iframe
              title={`PDF del DeCA ${document.urlToken}`}
              src={pdf.data.url}
              className="h-[32rem] w-full rounded-md border border-ink-200 dark:border-ink-800"
            />
          ) : (
            <p className="text-sm text-ink-600 dark:text-ink-300">
              El PDF no se carga solo al abrir la ficha: el binario nunca se sirve directamente
              desde la API, se pide una URL firmada del almacenamiento. Pulsa «Ver PDF».
            </p>
          )}

          <dl className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
            <div>
              <dt className="micro-label">Versiones</dt>
              <dd className="num mt-0.5">{chain.flatMap((entry) => entry.versions).length}</dd>
            </div>
            {document.transportStartDate ? (
              <div>
                <dt className="micro-label">Inicio del transporte</dt>
                <dd className="num mt-0.5">{formatDateTime(document.transportStartDate)}</dd>
              </div>
            ) : null}
            <div>
              <dt className="micro-label">Fin de transporte</dt>
              <dd className="num mt-0.5">{formatDate(document.transportEndDate)}</dd>
            </div>
            <div>
              <dt className="micro-label">Descarga desactivada</dt>
              <dd className="num mt-0.5">{formatDateTime(document.downloadDisabledAt)}</dd>
            </div>
            {currentVersion ? (
              <div className="col-span-2 sm:col-span-3">
                <dt className="micro-label">Huella de la versión</dt>
                <dd className="ident mt-0.5 break-all text-ink-600 dark:text-ink-300">
                  {formatHash(currentVersion.id)}
                </dd>
              </div>
            ) : null}
          </dl>
        </Card>

        <Card>
          <h2 className="micro-label mb-2">Descarga pública (QR)</h2>
          {qrDataUrl ? (
            <img
              src={qrDataUrl}
              alt={`Código QR de la URL pública del documento ${document.urlToken}`}
              className="mx-auto h-44 w-44 rounded bg-white p-1"
            />
          ) : (
            <div className="mx-auto h-44 w-44 animate-pulse rounded bg-ink-100 dark:bg-ink-800" />
          )}
          <p className="mt-3 break-all text-xs text-ink-600 dark:text-ink-300">
            <span className="ident">{publicUrl}</span>
          </p>
          <p className="mt-2 text-xs text-ink-500 dark:text-ink-400">
            Es la misma URL que va impresa en el PDF: se abre sin iniciar sesión, tal como exige la
            norma para el destinatario y los agentes de inspección.
          </p>
          <div className="mt-3 flex gap-2">
            <Button
              onClick={() => {
                if (!publicUrl) return;
                void navigator.clipboard.writeText(publicUrl).then(() => {
                  setCopied(true);
                  window.setTimeout(() => setCopied(false), 2000);
                });
              }}
            >
              {copied ? "Copiada" : "Copiar URL"}
            </Button>
            {/*
              «Abrir» reutiliza el mismo mecanismo que «Ver PDF»: descarga el
              binario a través del cliente API (con Authorization: Bearer) y
              abre la blob URL resultante en una pestaña nueva. No se puede
              enlazar directamente a `/api/v1/internal/deca/:id/pdf` porque el
              navegador no adjunta headers personalizados en pestañas abiertas
              con window.open o en iframes — el servidor devolvería 401.
            */}
            <Button
              disabled={pdf.isPending}
              onClick={() => {
                pdf.mutate(undefined, {
                  onSuccess: (result) => {
                    if (result.url) window.open(result.url, "_blank", "noopener,noreferrer");
                  },
                });
              }}
            >
              {pdf.isPending ? "Abriendo…" : "Abrir"}
            </Button>
          </div>
        </Card>
      </div>

      {dataSnapshot ? (
        <section className="mt-6 space-y-4">
          <h2 className="micro-label mb-2">Datos del documento</h2>

          {/* Partes intervinientes */}
          <Card>
            <h3 className="micro-label mb-3">Partes intervinientes</h3>
            <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <dt className="micro-label">Cargador contractual</dt>
                <dd className="mt-0.5 text-ink-800 dark:text-ink-100">
                  <span className="font-medium">{dataSnapshot.cargadorContractual.name}</span>
                  <br />
                  <span className="ident text-ink-500 dark:text-ink-400">{dataSnapshot.cargadorContractual.nif}</span>
                  {dataSnapshot.cargadorContractual.address ? (
                    <>
                      <br />
                      <span className="text-ink-500 dark:text-ink-400">{dataSnapshot.cargadorContractual.address}</span>
                    </>
                  ) : null}
                </dd>
              </div>
              <div>
                <dt className="micro-label">Transportista efectivo</dt>
                <dd className="mt-0.5 text-ink-800 dark:text-ink-100">
                  <span className="font-medium">{dataSnapshot.transportistaEfectivo.name}</span>
                  <br />
                  <span className="ident text-ink-500 dark:text-ink-400">{dataSnapshot.transportistaEfectivo.nif}</span>
                  {dataSnapshot.transportistaEfectivo.address ? (
                    <>
                      <br />
                      <span className="text-ink-500 dark:text-ink-400">{dataSnapshot.transportistaEfectivo.address}</span>
                    </>
                  ) : null}
                </dd>
              </div>
              {dataSnapshot.operadorIntermedio ? (
                <div>
                  <dt className="micro-label">Operador intermedio</dt>
                  <dd className="mt-0.5 text-ink-800 dark:text-ink-100">
                    <span className="font-medium">{dataSnapshot.operadorIntermedio.name}</span>
                    <br />
                    <span className="ident text-ink-500 dark:text-ink-400">{dataSnapshot.operadorIntermedio.nif}</span>
                  </dd>
                </div>
              ) : null}
              {dataSnapshot.vehiculo ? (
                <div>
                  <dt className="micro-label">Vehículo</dt>
                  <dd className="num mt-0.5 text-ink-800 dark:text-ink-100">
                    {dataSnapshot.vehiculo.plateTractor}
                    {dataSnapshot.vehiculo.plateTrailer ? ` + ${dataSnapshot.vehiculo.plateTrailer}` : ""}
                  </dd>
                </div>
              ) : null}
            </dl>
          </Card>

          {/* Envíos */}
          <Card>
            <h3 className="micro-label mb-3">Envíos ({dataSnapshot.envios.length})</h3>
            <ol className="space-y-4">
              {dataSnapshot.envios.map((envio, index) => (
                <li key={index} className="border-b border-ink-100 pb-4 last:border-0 last:pb-0 dark:border-ink-800">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-400">
                    Envío {index + 1}
                  </p>
                  <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
                    <div>
                      <dt className="micro-label">Origen</dt>
                      <dd className="mt-0.5">{envio.origen.lugar}</dd>
                    </div>
                    <div>
                      <dt className="micro-label">Destino</dt>
                      <dd className="mt-0.5">{envio.destino.lugar}</dd>
                    </div>
                    {envio.destinatario ? (
                      <div>
                        <dt className="micro-label">Destinatario</dt>
                        <dd className="mt-0.5">
                          <span className="font-medium">{envio.destinatario.name}</span>
                          {" · "}
                          <span className="ident text-ink-500 dark:text-ink-400">{envio.destinatario.nif}</span>
                        </dd>
                      </div>
                    ) : null}
                    <div>
                      <dt className="micro-label">Mercancía</dt>
                      <dd className="mt-0.5">
                        {envio.mercancia.descripcion}
                        {envio.adr ? <>{" "}<AdrPlacard /></> : null}
                      </dd>
                    </div>
                    <div>
                      <dt className="micro-label">Peso neto</dt>
                      <dd className="num mt-0.5">
                        {envio.mercancia.pesoNetoKg != null ? `${envio.mercancia.pesoNetoKg} kg` : "—"}
                      </dd>
                    </div>
                    {envio.mercancia.pesoBrutoKg != null ? (
                      <div>
                        <dt className="micro-label">Peso bruto</dt>
                        <dd className="num mt-0.5">{envio.mercancia.pesoBrutoKg} kg</dd>
                      </div>
                    ) : null}
                    {envio.mercancia.bultos ? (
                      <div>
                        <dt className="micro-label">Bultos</dt>
                        <dd className="num mt-0.5">{envio.mercancia.bultos}</dd>
                      </div>
                    ) : null}
                    {envio.mercancia.observaciones ? (
                      <div className="sm:col-span-2">
                        <dt className="micro-label">Observaciones</dt>
                        <dd className="mt-0.5 italic text-ink-600 dark:text-ink-300">
                          {envio.mercancia.observaciones}
                        </dd>
                      </div>
                    ) : null}
                    {envio.adr ? (
                      <div className="sm:col-span-2 mt-1 rounded-md border border-orange-300 bg-orange-50 p-3 dark:border-orange-800 dark:bg-orange-950/30">
                        <dt className="micro-label mb-2 text-orange-700 dark:text-orange-400">Mercancía peligrosa (ADR)</dt>
                        <dd>
                          <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
                            {envio.adr.numeroOnu ? (
                              <div>
                                <dt className="micro-label">Nº ONU</dt>
                                <dd className="ident mt-0.5 font-medium">{envio.adr.numeroOnu}</dd>
                              </div>
                            ) : null}
                            {envio.adr.denominacion ? (
                              <div>
                                <dt className="micro-label">Denominación</dt>
                                <dd className="mt-0.5">{envio.adr.denominacion}</dd>
                              </div>
                            ) : null}
                            {envio.adr.clase ? (
                              <div>
                                <dt className="micro-label">Clase de peligro</dt>
                                <dd className="ident mt-0.5">{envio.adr.clase}</dd>
                              </div>
                            ) : null}
                            {envio.adr.grupoEmbalaje ? (
                              <div>
                                <dt className="micro-label">Grupo de embalaje</dt>
                                <dd className="ident mt-0.5">{envio.adr.grupoEmbalaje}</dd>
                              </div>
                            ) : null}
                            {envio.adr.restriccionTunel ? (
                              <div>
                                <dt className="micro-label">Restricción de túnel</dt>
                                <dd className="ident mt-0.5">{envio.adr.restriccionTunel}</dd>
                              </div>
                            ) : null}
                          </dl>
                        </dd>
                      </div>
                    ) : null}
                  </dl>
                </li>
              ))}
            </ol>
          </Card>
        </section>
      ) : null}

      {/* ──────────────── Sección: Asignaciones ──────────────── */}
      {document.status === "ACTIVO" && (
        <section className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <h2 className="micro-label">Asignaciones</h2>
            {!assignOpen ? (
              <Button onClick={() => { setAssignOpen(true); setAssignError(null); setAssignSuccess(null); }}>
                ✏️ Editar asignaciones
              </Button>
            ) : (
              <Button onClick={() => setAssignOpen(false)}>Cancelar</Button>
            )}
          </div>

          {assignSuccess ? (
            <div className="mb-3">
              <Notice tone="ok">{assignSuccess}</Notice>
            </div>
          ) : null}

          {!assignOpen ? (
            /* Vista resumen de asignaciones actuales */
            <Card>
              <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <dt className="micro-label">Conductor</dt>
                  <dd className="mt-0.5 text-ink-800 dark:text-ink-100">
                    {document.driverId
                      ? (drivers.data?.find((d) => d.id === document.driverId)?.fullName ?? (
                          <span className="ident text-ink-500">ID: {document.driverId.slice(0, 8)}…</span>
                        ))
                      : <span className="text-ink-400 dark:text-ink-500">—</span>}
                  </dd>
                </div>
                <div>
                  <dt className="micro-label">Vehículo</dt>
                  <dd className="num mt-0.5 text-ink-800 dark:text-ink-100">
                    {dataSnapshot?.vehiculo
                      ? `${dataSnapshot.vehiculo.plateTractor}${dataSnapshot.vehiculo.plateTrailer ? ` + ${dataSnapshot.vehiculo.plateTrailer}` : ""}`
                      : <span className="text-ink-400 dark:text-ink-500">—</span>}
                  </dd>
                </div>
                <div>
                  <dt className="micro-label">Operador intermedio</dt>
                  <dd className="mt-0.5 text-ink-800 dark:text-ink-100">
                    {dataSnapshot?.operadorIntermedio
                      ? dataSnapshot.operadorIntermedio.name
                      : <span className="text-ink-400 dark:text-ink-500">—</span>}
                  </dd>
                </div>
                <div>
                  <dt className="micro-label">Destinatario (envío 1)</dt>
                  <dd className="mt-0.5 text-ink-800 dark:text-ink-100">
                    {dataSnapshot?.envios?.[0]?.destinatario
                      ? dataSnapshot.envios[0].destinatario.name
                      : <span className="text-ink-400 dark:text-ink-500">—</span>}
                  </dd>
                </div>
              </dl>
            </Card>
          ) : (
            /* Formulario de edición de asignaciones */
            <Card>
              {assignError ? (
                <div className="mb-4">
                  <Notice tone="danger">{assignError}</Notice>
                </div>
              ) : null}

              <div className="space-y-5">
                {/* Conductor */}
                <div>
                  <label className="micro-label mb-1 block">Conductor</label>
                  <select
                    className="w-full rounded-md border border-ink-300 bg-white px-3 py-2 text-sm text-ink-800 dark:border-ink-700 dark:bg-ink-900 dark:text-ink-100"
                    value={assignDriverId}
                    onChange={(e) => setAssignDriverId(e.target.value)}
                  >
                    <option value="">— Sin cambios —</option>
                    <option value="__clear__">× Quitar conductor</option>
                    {(drivers.data ?? []).map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.fullName} ({d.email})
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">
                    Cambiar solo el conductor no genera nueva versión del PDF.
                  </p>
                </div>

                {/* Vehículo */}
                <div>
                  <label className="micro-label mb-1 block">Vehículo</label>
                  <select
                    className="w-full rounded-md border border-ink-300 bg-white px-3 py-2 text-sm text-ink-800 dark:border-ink-700 dark:bg-ink-900 dark:text-ink-100"
                    value={assignVehicleId}
                    onChange={(e) => setAssignVehicleId(e.target.value)}
                  >
                    <option value="">— Sin cambios —</option>
                    <option value="__clear__">× Quitar vehículo</option>
                    {(vehicles.data?.items ?? []).filter((v) => v.isActive).map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.plateTractor}{v.plateTrailer ? ` + ${v.plateTrailer}` : ""}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Operador intermedio */}
                <div>
                  <label className="micro-label mb-1 block">Operador intermedio</label>
                  <select
                    className="w-full rounded-md border border-ink-300 bg-white px-3 py-2 text-sm text-ink-800 dark:border-ink-700 dark:bg-ink-900 dark:text-ink-100"
                    value={assignOperadorMode}
                    onChange={(e) => {
                      setAssignOperadorMode(e.target.value as "" | "catalog" | "clear");
                      setAssignOperadorId("");
                    }}
                  >
                    <option value="">— Sin cambios —</option>
                    <option value="clear">× Quitar operador</option>
                    <option value="catalog">Seleccionar del catálogo…</option>
                  </select>
                  {assignOperadorMode === "catalog" ? (
                    <select
                      className="mt-2 w-full rounded-md border border-ink-300 bg-white px-3 py-2 text-sm text-ink-800 dark:border-ink-700 dark:bg-ink-900 dark:text-ink-100"
                      value={assignOperadorId}
                      onChange={(e) => setAssignOperadorId(e.target.value)}
                    >
                      <option value="">— Elige una contraparte —</option>
                      {(counterparties.data?.items ?? []).filter((cp) => cp.isActive).map((cp) => (
                        <option key={cp.id} value={cp.id}>
                          {cp.name} ({cp.nif})
                        </option>
                      ))}
                    </select>
                  ) : null}
                </div>

                {/* Destinatario por envío */}
                {dataSnapshot && dataSnapshot.envios.length > 0 ? (
                  <div>
                    <label className="micro-label mb-1 block">Destinatario de envío</label>
                    {dataSnapshot.envios.length > 1 ? (
                      <select
                        className="mb-2 w-full rounded-md border border-ink-300 bg-white px-3 py-2 text-sm text-ink-800 dark:border-ink-700 dark:bg-ink-900 dark:text-ink-100"
                        value={assignShipmentIndex}
                        onChange={(e) => setAssignShipmentIndex(Number(e.target.value))}
                      >
                        {dataSnapshot.envios.map((envio, i) => (
                          <option key={i} value={i}>
                            Envío {i + 1}: {envio.origen.lugar} → {envio.destino.lugar}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <p className="mb-1 text-xs text-ink-500 dark:text-ink-400">
                        Envío 1: {dataSnapshot.envios[0]?.origen.lugar} → {dataSnapshot.envios[0]?.destino.lugar}
                      </p>
                    )}
                    <select
                      className="w-full rounded-md border border-ink-300 bg-white px-3 py-2 text-sm text-ink-800 dark:border-ink-700 dark:bg-ink-900 dark:text-ink-100"
                      value={assignDestinatarioMode}
                      onChange={(e) => {
                        setAssignDestinatarioMode(e.target.value as "" | "catalog" | "clear");
                        setAssignDestinatarioId("");
                      }}
                    >
                      <option value="">— Sin cambios —</option>
                      <option value="clear">× Quitar destinatario</option>
                      <option value="catalog">Seleccionar del catálogo…</option>
                    </select>
                    {assignDestinatarioMode === "catalog" ? (
                      <select
                        className="mt-2 w-full rounded-md border border-ink-300 bg-white px-3 py-2 text-sm text-ink-800 dark:border-ink-700 dark:bg-ink-900 dark:text-ink-100"
                        value={assignDestinatarioId}
                        onChange={(e) => setAssignDestinatarioId(e.target.value)}
                      >
                        <option value="">— Elige una contraparte —</option>
                        {(counterparties.data?.items ?? []).filter((cp) => cp.isActive).map((cp) => (
                          <option key={cp.id} value={cp.id}>
                            {cp.name} ({cp.nif})
                          </option>
                        ))}
                      </select>
                    ) : null}
                  </div>
                ) : null}

                {/* Motivo opcional: sale en el histórico de cambios del PDF */}
                <div>
                  <label className="micro-label mb-1 block" htmlFor="assign-reason">
                    Motivo del cambio (opcional)
                  </label>
                  <input
                    id="assign-reason"
                    type="text"
                    maxLength={500}
                    className="w-full rounded-md border border-ink-300 bg-white px-3 py-2 text-sm text-ink-800 dark:border-ink-700 dark:bg-ink-900 dark:text-ink-100"
                    placeholder="Ej.: cambio de tractora por avería"
                    value={assignReason}
                    onChange={(e) => setAssignReason(e.target.value)}
                  />
                  <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">
                    Aparece en el histórico de cambios del PDF. Si lo dejas vacío se indica «Reasignación de campos desde el panel».
                  </p>
                </div>

                {/* Botón guardar */}
                <div className="flex gap-2 pt-2 border-t border-ink-100 dark:border-ink-800">
                  <Button
                    variant="primary"
                    disabled={updateAssignments.isPending}
                    onClick={() => {
                      setAssignError(null);
                      setAssignSuccess(null);

                      // Construir payload
                      const payload: Parameters<typeof updateAssignments.mutate>[0] = { id: document.id };
                      let hasChange = false;

                      if (assignDriverId === "__clear__") {
                        payload.driverId = null;
                        hasChange = true;
                      } else if (assignDriverId) {
                        payload.driverId = assignDriverId;
                        hasChange = true;
                      }

                      if (assignVehicleId === "__clear__") {
                        payload.vehiculo = null;
                        hasChange = true;
                      } else if (assignVehicleId) {
                        payload.vehiculo = { vehicleId: assignVehicleId };
                        hasChange = true;
                      }

                      if (assignOperadorMode === "clear") {
                        payload.operadorIntermedio = null;
                        hasChange = true;
                      } else if (assignOperadorMode === "catalog" && assignOperadorId) {
                        payload.operadorIntermedio = { counterpartyId: assignOperadorId };
                        hasChange = true;
                      } else if (assignOperadorMode === "catalog" && !assignOperadorId) {
                        setAssignError("Selecciona una contraparte del catálogo para el operador.");
                        return;
                      }

                      if (assignDestinatarioMode === "clear") {
                        const dests: (null | undefined)[] = [];
                        dests[assignShipmentIndex] = null;
                        payload.destinatarios = dests;
                        hasChange = true;
                      } else if (assignDestinatarioMode === "catalog" && assignDestinatarioId) {
                        const dests: Parameters<typeof updateAssignments.mutate>[0]["destinatarios"] = [];
                        dests![assignShipmentIndex] = { counterpartyId: assignDestinatarioId };
                        payload.destinatarios = dests;
                        hasChange = true;
                      } else if (assignDestinatarioMode === "catalog" && !assignDestinatarioId) {
                        setAssignError("Selecciona una contraparte del catálogo para el destinatario.");
                        return;
                      }

                      if (!hasChange) {
                        setAssignError("No has seleccionado ningún cambio.");
                        return;
                      }

                      const reason = assignReason.trim();
                      if (reason) payload.changeReason = reason;

                      updateAssignments.mutate(payload, {
                        onSuccess: (result) => {
                          const msg =
                            result.mode === "driver_only"
                              ? "Conductor actualizado correctamente."
                              : `Asignaciones guardadas. Nueva versión del PDF generada (v${result.versionNumber ?? "?"}).`;
                          setAssignSuccess(msg);
                          setAssignOpen(false);
                          // Reset formulario
                          setAssignDriverId("");
                          setAssignVehicleId("");
                          setAssignOperadorMode("");
                          setAssignOperadorId("");
                          setAssignDestinatarioMode("");
                          setAssignDestinatarioId("");
                          setAssignReason("");
                        },
                        onError: (err) =>
                          setAssignError(err instanceof Error ? err.message : "Error al guardar las asignaciones"),
                      });
                    }}
                  >
                    {updateAssignments.isPending ? "Guardando…" : "💾 Guardar asignaciones"}
                  </Button>
                  <Button onClick={() => { setAssignOpen(false); setAssignError(null); }}>Cancelar</Button>
                </div>
              </div>
            </Card>
          )}
        </section>
      )}

      <section className="mt-6">
        <h2 className="micro-label mb-2">Historial del documento</h2>
        <p className="mb-3 max-w-3xl text-sm text-ink-600 dark:text-ink-300">
          Se muestran las <strong>dos cadenas</strong>: las versiones del mismo documento (misma URL
          y mismo QR) y los documentos que lo sustituyen. Sustituir nunca borra el anterior.
        </p>
        <ol className="space-y-2">
          {chain.map((entry, index) => (
            <li key={entry.decaDocumentId} className="surface flex flex-col gap-2 p-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="ident font-semibold">{index + 1}.</span>
                  <span className="ident">{entry.urlToken}</span>
                  <Chip tone={decaStatusTone(entry.status)}>{decaStatusLabel(entry.status)}</Chip>
                  {entry.isCurrent ? <Chip tone="ok">Documento abierto</Chip> : null}
                </p>
                <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">
                  Emitido el {formatDateTime(entry.createdAt)}
                  {entry.relatedDocumentId ? " · sustituye a un documento anterior" : ""}
                </p>
                <ul className="mt-2 space-y-2">
                  {entry.versions.map((version) => (
                    <li key={version.id} className="text-xs text-ink-600 dark:text-ink-300">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="ident">v{version.versionNumber}</span>
                        <span>
                          · {formatDateTime(version.createdAt)}
                          {version.changeReason ? ` · ${version.changeReason}` : ""}
                        </span>
                        {version.isCurrent && entry.versions.length > 1 ? <Chip tone="ok">Vigente</Chip> : null}
                        <button
                          type="button"
                          className="text-brand-700 hover:underline disabled:opacity-50 dark:text-brand-300"
                          disabled={versionPdf.isPending}
                          onClick={() => {
                            versionPdf.mutate(
                              { documentId: entry.decaDocumentId, versionId: version.id },
                              {
                                onSuccess: (result) => {
                                  window.open(result.url, "_blank", "noopener,noreferrer");
                                },
                              },
                            );
                          }}
                        >
                          Ver PDF
                        </button>
                      </div>
                      {version.changes.length > 0 ? (
                        <ul className="mt-1 list-disc space-y-0.5 pl-8 text-ink-500 dark:text-ink-400">
                          {version.changes.map((change) => (
                            <li key={change}>{change}</li>
                          ))}
                        </ul>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
              {!entry.isCurrent ? (
                <Link to={`/deca/${entry.decaDocumentId}`} className="text-sm text-brand-700 hover:underline dark:text-brand-300">
                  Abrir
                </Link>
              ) : null}
            </li>
          ))}
        </ol>
      </section>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <IncidentPanel document={document} />
        <SignaturePanel
          documentId={document.id}
          signatures={signatures.data?.items ?? []}
          isLoading={signatures.isLoading}
          isAdmin={isAdmin}
        />
      </div>
    </>
  );
}
